import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";
import store from "@/store";
import Service, {
  RevisionAccessRequest,
  ActiveCheckResponse,
  RevisionScope,
  SubmitRevisionPayload
} from "@/services/RevisionAccessService";

export function activeKey(
  registerNo: string,
  documentType?: string | null,
  documentId?: number | null
): string {
  if (!documentType || !documentId) return registerNo;
  return `${registerNo}|${documentType}|${documentId}`;
}

function toScope(arg: string | RevisionScope): RevisionScope {
  if (typeof arg === "string") return { register_no: arg };
  const scope: RevisionScope = { register_no: arg.register_no };
  if (arg.document_type && arg.document_id) {
    scope.document_type = arg.document_type;
    scope.document_id = arg.document_id;
  }
  return scope;
}

interface ActiveSnapshot {
  has_active: boolean;
  has_pending: boolean;
  active: ActiveCheckResponse["active"];
  pending: ActiveCheckResponse["pending"];
  document?: ActiveCheckResponse["document"];
  fetchedAt: number;
}

@Module({
  store,
  dynamic: true,
  namespaced: true,
  name: "revisionAccess"
})
class RevisionAccessModule extends VuexModule {
  pendingList: RevisionAccessRequest[] = [];
  historyList: RevisionAccessRequest[] = [];
  myList: RevisionAccessRequest[] = [];

  // Keyed by register_no — hasil cek dari /active.
  activeByRegister: Record<string, ActiveSnapshot> = {};

  loading = false;

  @Mutation SET_PENDING(list: RevisionAccessRequest[]) {
    this.pendingList = list;
  }
  @Mutation SET_HISTORY(list: RevisionAccessRequest[]) {
    this.historyList = list;
  }
  @Mutation SET_MY(list: RevisionAccessRequest[]) {
    this.myList = list;
  }
  @Mutation SET_ACTIVE(payload: { registerNo: string; data: ActiveSnapshot }) {
    this.activeByRegister = {
      ...this.activeByRegister,
      [payload.registerNo]: payload.data
    };
  }
  @Mutation SET_LOADING(v: boolean) {
    this.loading = v;
  }

  @Action({ rawError: true })
  async fetchActive(arg: string | RevisionScope) {
    const scope = toScope(arg);
    if (!scope.register_no) return;
    try {
      const res = await Service.fetchActive(scope);
      this.SET_ACTIVE({
        registerNo: activeKey(
          scope.register_no,
          scope.document_type,
          scope.document_id
        ),
        data: {
          has_active: res.has_active,
          has_pending: res.has_pending,
          active: res.active,
          pending: res.pending,
          document: res.document,
          fetchedAt: Date.now()
        }
      });
    } catch (e) {
      // Silently swallow — banner tetap render readonly state.
    }
  }

  @Action({ rawError: true })
  async submitRequest(payload: SubmitRevisionPayload) {
    this.SET_LOADING(true);
    try {
      const data = await Service.submitRequest(payload);
      // Refresh active snapshot supaya banner update ke "pending".
      await this.fetchActive(toScope(payload));
      return data;
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action({ rawError: true })
  async fetchPending() {
    this.SET_LOADING(true);
    try {
      const list = await Service.fetchAll({ status: "pending" });
      this.SET_PENDING(list);
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action({ rawError: true })
  async fetchHistory(status: string) {
    this.SET_LOADING(true);
    try {
      const list = await Service.fetchAll({ status });
      this.SET_HISTORY(list);
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action({ rawError: true })
  async fetchMy() {
    this.SET_LOADING(true);
    try {
      const list = await Service.fetchMy();
      this.SET_MY(list);
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action({ rawError: true })
  async approve(payload: {
    id: number;
    granted_minutes?: number;
    note?: string;
  }) {
    this.SET_LOADING(true);
    try {
      await Service.approve(payload.id, {
        granted_minutes: payload.granted_minutes,
        note: payload.note
      });
      await this.fetchPending();
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action({ rawError: true })
  async reject(payload: { id: number; rejection_reason: string }) {
    this.SET_LOADING(true);
    try {
      await Service.reject(payload.id, {
        rejection_reason: payload.rejection_reason
      });
      await this.fetchPending();
    } finally {
      this.SET_LOADING(false);
    }
  }
}

export default getModule(RevisionAccessModule);
